import {
  Box,
  Text,
  styled,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  Gavel,
  RecordVoiceOver,
  Person,
  Egg,
  Groups,
} from '@mui/icons-material';
import { useState, useEffect, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  COURT_CASES_V2_ROUTES,
  LEGAL_INTEL_SECTION_HEADING,
  LEGAL_INTEL_SECTION_QUERY_KEY,
  type LegalIntelSection,
  legalIntelSectionForCourtCasesPath,
  parseLegalIntelSection,
  withLegalIntelSection,
} from '../constants';

import { CasesSearch } from './CasesSearch';
import { ClassActionLawsuitSearch } from './ClassActionLawsuitSearch';
import { JudgeSearch } from './JudgeSearch';
import { getCourtCasesV2MainNavBreadcrumbSegments } from './mainNavBreadcrumbSegments';
import { OpinionsSearch } from './OpinionsSearch';
import { OralArgumentsSearch } from './OralArgumentsSearch';
import { RecallsSearch } from './RecallsSearch';
import {
  LegalResearchTab,
  LegalResearchTabsNavigation,
  LegalSearchBreadcrumbs,
} from './shared';

const CourtCasesV2Shell = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const LegalResearchHubTitle = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  fontFamily: theme.typography.fontFamily,
})) as typeof Text;

const TAB_ICONS: Record<string, ReactElement> = {
  'Court Cases': <Gavel fontSize="large" />,
  'Class Actions': <Groups fontSize="large" />,
  Opinions: <Person fontSize="large" />,
  'Oral Arguments': <RecordVoiceOver fontSize="large" />,
  'Judge Search': <Person fontSize="large" />,
  Recalls: <Egg fontSize="large" />,
};

type CourtCasesV2TabConfig = {
  label: string;
  content: ReactElement;
  path: string;
  isEnabled: boolean;
  legalIntelSection: LegalIntelSection;
};

export const CourtCasesParentV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Feature flags for gating tabs
  const isOralArgumentsEnabled = useFeatureIsOn('OAR-1982');
  const isJudgeSearchEnabled = useFeatureIsOn('OAR-1983');

  const pathnameSection = legalIntelSectionForCourtCasesPath(location.pathname);

  // Define all tabs with their enabled status and Legal Intelligence hub section
  const allTabs: CourtCasesV2TabConfig[] = [
    {
      label: 'Court Cases',
      content: (
        <div key="cases-tab">
          <CasesSearch />
        </div>
      ),
      path: COURT_CASES_V2_ROUTES.CASES,
      isEnabled: true,
      legalIntelSection: 'legal-research',
    },
    {
      label: 'Class Actions',
      content: (
        <div key="class-action-lawsuits-tab">
          <ClassActionLawsuitSearch />
        </div>
      ),
      path: COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUITS,
      isEnabled: true,
      legalIntelSection: 'class-actions-recalls',
    },
    {
      label: 'Opinions',
      content: (
        <div key="opinions-tab">
          <OpinionsSearch />
        </div>
      ),
      path: COURT_CASES_V2_ROUTES.OPINIONS,
      isEnabled: true,
      legalIntelSection: 'legal-research',
    },
    {
      label: 'Oral Arguments',
      content: (
        <div key="oral-arguments-tab">
          <OralArgumentsSearch />
        </div>
      ),
      path: COURT_CASES_V2_ROUTES.ORAL_ARGUMENTS,
      isEnabled: isOralArgumentsEnabled,
      legalIntelSection: 'legal-research',
    },
    {
      label: 'Judge Search',
      content: (
        <div key="judge-search-tab">
          <JudgeSearch />
        </div>
      ),
      path: COURT_CASES_V2_ROUTES.JUDGE_SEARCH,
      isEnabled: isJudgeSearchEnabled,
      legalIntelSection: 'legal-research',
    },
    {
      label: 'Recalls',
      content: (
        <div key="recalls-tab">
          <RecallsSearch />
        </div>
      ),
      path: COURT_CASES_V2_ROUTES.RECALLS,
      isEnabled: true,
      legalIntelSection: 'class-actions-recalls',
    },
  ];

  // Filter to tabs enabled by feature flags and matching the active hub section (pathname)
  const enabledTabs = allTabs.filter(
    (tab) => tab.isEnabled && tab.legalIntelSection === pathnameSection
  );

  // Get tab index from pathname
  const getTabIndexFromPath = (
    pathname: string,
    availableTabs: typeof enabledTabs
  ): number => {
    // Handle base route mapping to Cases
    if (
      pathname === COURT_CASES_V2_ROUTES.CASES ||
      pathname === COURT_CASES_V2_ROUTES.BASE
    ) {
      return 0;
    }

    if (
      pathname.startsWith(
        `${COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUIT_REPORT}/`
      )
    ) {
      const caIndex = availableTabs.findIndex(
        (tab) => tab.path === COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUITS
      );
      return caIndex >= 0 ? caIndex : 0;
    }

    const index = availableTabs.findIndex((tab) => tab.path === pathname);
    return index >= 0 ? index : 0;
  };

  const [activeTab, setActiveTab] = useState(0);

  // Initialize active tab from URL once enabledTabs is available
  useEffect(() => {
    if (enabledTabs.length > 0 && activeTab === 0) {
      const tabFromPath = getTabIndexFromPath(location.pathname, enabledTabs);
      setActiveTab(tabFromPath);
    }
  }, [
    enabledTabs.length,
    location.pathname,
    pathnameSection,
    isOralArgumentsEnabled,
    isJudgeSearchEnabled,
  ]);

  // Redirect if user is on a disabled tab route
  useEffect(() => {
    if (
      location.pathname === COURT_CASES_V2_ROUTES.ORAL_ARGUMENTS &&
      !isOralArgumentsEnabled
    ) {
      navigate(
        withLegalIntelSection(COURT_CASES_V2_ROUTES.CASES, 'legal-research')
      );
    } else if (
      location.pathname === COURT_CASES_V2_ROUTES.JUDGE_SEARCH &&
      !isJudgeSearchEnabled
    ) {
      navigate(
        withLegalIntelSection(COURT_CASES_V2_ROUTES.CASES, 'legal-research')
      );
    }
  }, [
    location.pathname,
    isOralArgumentsEnabled,
    isJudgeSearchEnabled,
    navigate,
  ]);

  // Update tab when URL changes
  useEffect(() => {
    const tabFromPath = getTabIndexFromPath(location.pathname, enabledTabs);
    if (tabFromPath !== activeTab && enabledTabs.length > 0) {
      setActiveTab(tabFromPath);
    }
  }, [
    location.pathname,
    activeTab,
    pathnameSection,
    isOralArgumentsEnabled,
    isJudgeSearchEnabled,
  ]);

  const handleTabChange = (newTab: number) => {
    setActiveTab(newTab);

    const selectedTab = enabledTabs[newTab];
    if (selectedTab) {
      const section = legalIntelSectionForCourtCasesPath(selectedTab.path);
      const nextParams = new URLSearchParams(location.search);
      nextParams.set(LEGAL_INTEL_SECTION_QUERY_KEY, section);
      const search = nextParams.toString();
      navigate({
        pathname: selectedTab.path,
        search: search ? `?${search}` : '',
      });
    }
  };

  const section = parseLegalIntelSection(location.search, location.pathname);

  return (
    <CourtCasesV2Shell>
      <LegalSearchBreadcrumbs
        segments={getCourtCasesV2MainNavBreadcrumbSegments(
          location.pathname,
          location.search
        )}
      />
      <LegalResearchHubTitle component="h1" variant="h1">
        {LEGAL_INTEL_SECTION_HEADING[section]}
      </LegalResearchHubTitle>
      <LegalResearchTabsNavigation
        externalActiveTab={activeTab}
        onTabChanged={handleTabChange}
        tab={(props) => (
          <LegalResearchTab
            {...props}
            icon={TAB_ICONS[props.label]}
            iconPosition={isMobile ? 'top' : 'start'}
          />
        )}
        tabs={enabledTabs.map((tab) => ({
          label: tab.label,
          content: tab.content,
        }))}
      />
    </CourtCasesV2Shell>
  );
};
